'use client';

import { c as _c } from "react/compiler-runtime";
import clsx from 'clsx';
import Image from 'next/image';
import { FC, MouseEvent as ReactMouseEvent, useEffect, useRef } from 'react';
import AnimatedLink from '@/components/shared/AnimatedLink';
import BookACallButton from '@/components/shared/Footer/BookACallButton';
import { FooterDto } from '@/components/shared/Footer/types';
import VideoBackgroundFooter from '@/components/shared/Footer/VideoBackground';
import StrapiRichContent from '@/components/StrapiRichContent';
import { isArrayNotEmpty } from '@/helpers/array';
import { scrollToHash } from '@/helpers/scrollToHash';
import useDevice from '@/hooks/useDevice';
import { useModal } from '@/hooks/useModal';
import { gsap } from '@/lib/gsap';
import FooterForm from './Form';
import css from './style.module.scss';
import { useFooterTitleAnimation } from './useFooterTitleAnimation';
import { FooterVariant } from './variants';
interface Props {
  data: FooterDto;
  variant?: FooterVariant;
}
const Footer: FC<Props> = t0 => {
  const $ = _c(44);
  const {
    data,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "default" : t1;
  const {
    title,
    cta,
    socials,
    navigation
  } = data;
  const {
    openModal
  } = useModal();
  const device = useDevice();
  const cardRef = useRef(null);
  const isIndustriesLobbyPage = variant === "industries-lobby";
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      cardRef,
      titleSelector: `.${css.title}`
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  useFooterTitleAnimation(t2);
  let t3;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const card = cardRef.current;
      if (!card) {
        return;
      }
      const handleMouseMove = e => {
        const {
          left,
          top
        } = card.getBoundingClientRect();
        const x = e.clientX - left;
        const y = e.clientY - top;
        gsap.to(card, {
          "--x": `${x}px`,
          "--y": `${y}px`,
          duration: 0.2
        });
      };
      card.addEventListener("mousemove", handleMouseMove);
      return () => card.removeEventListener("mousemove", handleMouseMove);
    };
    t4 = [];
    $[1] = t3;
    $[2] = t4;
  } else {
    t3 = $[1];
    t4 = $[2];
  }
  useEffect(t3, t4);
  const handleNavigationClick = _temp;
  const t5 = isIndustriesLobbyPage && css.industriesLobbyPage;
  let t6;
  if ($[3] !== t5) {
    t6 = clsx(css.wrapper, t5);
    $[3] = t5;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== device) {
    t7 = device === "desktop" && <div className={css.glowWrapper} />;
    $[5] = device;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== title) {
    t8 = title ? <StrapiRichContent content={JSON.parse(JSON.stringify(title))} /> : null;
    $[7] = title;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== t8) {
    t9 = <div className={css.title}>{t8}</div>;
    $[9] = t8;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== cta || $[12] !== openModal) {
    t10 = cta ? <BookACallButton className={css.bookACallButton} preview={cta.image} onClick={() => openModal("CALENDAR_BOOKING")}>{cta?.text}</BookACallButton> : null;
    $[11] = cta;
    $[12] = openModal;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t10 || $[15] !== t9) {
    t11 = <div className={css.leftSide}>{t9}{t10}</div>;
    $[14] = t10;
    $[15] = t9;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className={css.rightSide}><FooterForm /></div>;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t11) {
    t13 = <div>{t11}{t12}</div>;
    $[18] = t11;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== t13 || $[21] !== t7) {
    t14 = <div ref={cardRef} className={css.formWrapper}>{t7}{t13}</div>;
    $[20] = t13;
    $[21] = t7;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== device || $[24] !== socials) {
    t15 = isArrayNotEmpty(socials) && socials.map((t16, index) => {
      const {
        id,
        name,
        url
      } = t16;
      return <li key={`${id}-${index}`}>{device === "desktop" ? <AnimatedLink href={url} target="_blank" rel="noreferrer">{name}</AnimatedLink> : <a href={url} target="_blank" rel="noreferrer"><Image src={`/assets/icons/socials/${name}.svg`} width="28" height="28" alt={name} /></a>}</li>;
    });
    $[23] = device;
    $[24] = socials;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t15) {
    t16 = <ul className={css.mediaList}>{t15}</ul>;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== navigation) {
    t17 = isArrayNotEmpty(navigation) && navigation.map((t18, index_0) => {
      const {
        navigationItem
      } = t18;
      return <ul key={index_0} className={css.navList}>{navigationItem?.map(t19 => {
          const {
            id: id_0,
            text,
            url: url_0
          } = t19;
          return <li key={id_0}><AnimatedLink target={url_0.includes("http") ? "_blank" : "_self"} href={url_0} onClick={handleNavigationClick}>{text}</AnimatedLink></li>;
        })}</ul>;
    });
    $[28] = navigation;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== t17) {
    t18 = <div className={css.navContainer}>{t17}</div>;
    $[30] = t17;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== device) {
    t19 = device === "desktop" && <span className={css.copy}>{new Date().getFullYear()}</span>;
    $[32] = device;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== t16 || $[35] !== t18 || $[36] !== t19) {
    t20 = <div className={css.bottomWrapper}>{t16}{t18}{t19}</div>;
    $[34] = t16;
    $[35] = t18;
    $[36] = t19;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== t14 || $[39] !== t20) {
    t21 = <VideoBackgroundFooter>{t14}{t20}</VideoBackgroundFooter>;
    $[38] = t14;
    $[39] = t20;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== t21 || $[42] !== t6) {
    t22 = <footer id="layout-footer" className={t6}>{t21}</footer>;
    $[41] = t21;
    $[42] = t6;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  return t22;
};
export default Footer;
function _temp(event) {
  const href = event?.currentTarget?.getAttribute("href");
  if (href?.startsWith("#")) {
    event.preventDefault();
    scrollToHash(href);
  }
}